import { Button, Checkbox, Tag } from '@ads/components-react';
import {
  AcademicCapIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import type { AIADetails, CourseAccess, SelectiveProcess } from '../../../types/enrollment';

// ─── Local sub-components ────────────────────────────────────────────────────

function SectionCard({
  children,
  header,
}: {
  children: React.ReactNode;
  header?: React.ReactNode;
}) {
  return (
    <div className="rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface overflow-hidden">
      {header && (
        <div className="border-b-hairline border-ctx-layout-border px-xxs py-quark">{header}</div>
      )}
      <div className="px-xxs py-xxxs">{children}</div>
    </div>
  );
}

function SectionTitle({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between py-nano">
      <div className="flex items-center gap-mini">
        {Icon && <Icon className="h-xxxs w-xxxs text-ctx-content-placeholder" />}
        <div className="flex flex-col">
          <span className="ts-paragraph-xs !font-medium text-ctx-content-title">{title}</span>
          {description && (
            <span className="ts-caption-xxs text-ctx-content-placeholder">{description}</span>
          )}
        </div>
      </div>
      {action}
    </div>
  );
}

function InfoField({
  label,
  value,
  note,
}: {
  label: string;
  value: React.ReactNode;
  note?: string | null;
}) {
  return (
    <div className="flex flex-col gap-quark">
      <span className="ts-caption-xxs tracking-wider text-ctx-content-placeholder uppercase">
        {label}
      </span>
      <span className="ts-paragraph-xs !font-medium text-ctx-content-title">{value ?? '—'}</span>
      {note != null && <span className="ts-caption-xxs text-ctx-content-placeholder">{note}</span>}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('pt-BR');
}

function formatInterviewDateTime(dateStr: string): string {
  const d = new Date(dateStr);
  const date = d.toLocaleDateString('pt-BR');
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${date} · ${h}h${m}`;
}

function formatGrade(grade: number): string {
  return `${new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(grade)} / 10,00`;
}

// ─── Props ───────────────────────────────────────────────────────────────────

export interface EnrollmentAcademicTabProps {
  readonly startDate: string;
  readonly courseAccess: CourseAccess;
  readonly isIndicatedByStudent?: boolean;
  readonly aiaDetails?: AIADetails | null;
  readonly selectiveProcess: SelectiveProcess | null;
  readonly poleName: string | null;
  readonly poleId: number | null;
  readonly onEditCourseAccess?: () => void;
  readonly onEditSelectiveProcess?: () => void;
}

/**
 * Aba Acadêmico do detalhe de matrícula.
 * Exibe acesso ao curso, programa AIA/AIR e processo seletivo.
 */
export function EnrollmentAcademicTab({
  startDate,
  courseAccess,
  isIndicatedByStudent,
  aiaDetails,
  selectiveProcess,
  poleName,
  poleId,
  onEditCourseAccess,
  onEditSelectiveProcess,
}: EnrollmentAcademicTabProps) {
  const { t } = useTranslation('personManagement');

  const selectiveStatusTag = (status: SelectiveProcess['status']) => {
    if (status === 'approved') return 'positive' as const;
    if (status === 'rejected') return 'negative' as const;
    return 'helper' as const;
  };

  return (
    <div className="flex flex-col gap-xxs bg-ctx-layout-body py-xs">
      {/* ── Acesso ao curso ── */}
      <SectionCard
        header={
          <SectionTitle
            icon={AcademicCapIcon}
            title={t('enrollmentAcademic.sectionCourseAccessTitle')}
            description={t('enrollmentAcademic.sectionCourseAccessDescription')}
            action={
              <Button
                hierarchy="tertiary"
                size="sm"
                leadingIcon={PencilSquareIcon}
                onClick={onEditCourseAccess}
              >
                {t('enrollmentAcademic.editCourseAccess')}
              </Button>
            }
          />
        }
      >
        <div className="flex flex-col gap-xxxs">
          <Checkbox
            label={t('enrollmentAcademic.fieldReleased')}
            checked={courseAccess.isReleased ?? false}
            onCheckedChange={() => {}}
          />
          <div className="grid grid-cols-2 gap-xxxs md:grid-cols-4 mt-mini">
            <InfoField
              label={t('enrollmentAcademic.fieldReleasedUntil')}
              value={formatDate(courseAccess.releasedUntil ?? null)}
              note={t('enrollmentAcademic.fieldReleasedUntilNote')}
            />
            <InfoField
              label={t('enrollmentAcademic.fieldStartDate')}
              value={formatDate(startDate)}
            />
            <InfoField
              label={t('enrollmentAcademic.fieldReleasedDays')}
              value={
                courseAccess.releasedDays != null ? String(courseAccess.releasedDays) : undefined
              }
              note={t('enrollmentAcademic.fieldReleasedDaysNote')}
            />
            <div className="flex flex-col gap-quark">
              <span className="ts-caption-xxs tracking-wider text-ctx-content-placeholder uppercase">
                {t('enrollmentAcademic.fieldCurrentState')}
              </span>
              <div>
                <Tag
                  label={
                    (courseAccess.isReleased ?? false)
                      ? t('enrollmentAcademic.currentStateActive')
                      : t('enrollmentAcademic.currentStateInactive')
                  }
                  size="sm"
                  status={(courseAccess.isReleased ?? false) ? 'positive' : 'negative'}
                  custom={{ text: 'text-nowrap' }}
                />
              </div>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* ── Aluno Indica Aluno ── */}
      {(isIndicatedByStudent === true || aiaDetails != null) && (
        <SectionCard
          header={
            <SectionTitle
              icon={UserGroupIcon}
              title={t('enrollmentAcademic.sectionAiaTitle')}
              description={t('enrollmentAcademic.sectionAiaDescription')}
            />
          }
        >
          <div className="flex flex-col gap-xxxs">
            {aiaDetails != null && (
              <div className="grid grid-cols-2 gap-xxxs md:grid-cols-4">
                <InfoField
                  label={t('enrollmentAcademic.fieldIndicatedBy')}
                  value={t('enrollmentAcademic.fieldIndicatedByValue', {
                    name: aiaDetails.indicatedByName,
                  })}
                  note={t('enrollmentAcademic.fieldIndicatedByNote', {
                    role: aiaDetails.indicatedByRole,
                    class: aiaDetails.indicatedByClass,
                  })}
                />
                <InfoField
                  label={t('enrollmentAcademic.fieldFromForm')}
                  value={
                    aiaDetails.fromForm
                      ? t('enrollmentAcademic.fromFormYes')
                      : t('enrollmentAcademic.fromFormNo')
                  }
                  note={aiaDetails.fromFormTechnicalNote}
                />
                <InfoField
                  label={t('enrollmentAcademic.fieldHash')}
                  value={aiaDetails.hash}
                  note={t('enrollmentAcademic.fieldHashNote', {
                    date: formatDate(aiaDetails.hashGeneratedAt),
                  })}
                />
                <div className="flex flex-col gap-quark">
                  <span className="ts-caption-xxs tracking-wider text-ctx-content-placeholder uppercase">
                    {t('enrollmentAcademic.fieldProgramAccess')}
                  </span>
                  <div>
                    <Tag
                      label={t(
                        `enrollmentAcademic.programAccessStatus.${aiaDetails.programAccessStatus}`,
                      )}
                      size="sm"
                      status={
                        aiaDetails.programAccessStatus === 'active'
                          ? 'positive'
                          : aiaDetails.programAccessStatus === 'blocked'
                            ? 'negative'
                            : 'helper'
                      }
                      custom={{ text: 'text-nowrap' }}
                    />
                  </div>
                  {aiaDetails.programAccessNote && (
                    <span className="ts-caption-xxs text-ctx-content-placeholder">
                      {t('enrollmentAcademic.fieldProgramAccessNote', {
                        note: aiaDetails.programAccessNote,
                      })}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* AIA warning */}
            <div className="rounded-sm border-hairline bg-ctx-layout-body px-xxs py-xxxs mt-mini flex items-start gap-mini">
              <ExclamationTriangleIcon className="h-xxxs w-xxxs text-base-warning-700 shrink-0 mt-quark" />
              <p className="ts-paragraph-xs text-ctx-content-title">
                <span>{t('enrollmentAcademic.aiaWarningRulePre')}</span>
                <strong className="font-semibold">
                  {t('enrollmentAcademic.aiaWarningRuleBold')}
                </strong>
                <span>{t('enrollmentAcademic.aiaWarningRulePost')}</span>
                {poleId != null && poleName != null ? (
                  <>
                    <strong className="font-semibold">
                      {t('enrollmentAcademic.aiaWarningPoloName', {
                        polo: poleName,
                      })}
                    </strong>
                    <span>{t('enrollmentAcademic.aiaWarningPoloSuffix')}</span>
                  </>
                ) : (
                  <>
                    <strong className="font-semibold">
                      {t('enrollmentAcademic.aiaWarningNoPoloName')}
                    </strong>
                    <span>{t('enrollmentAcademic.aiaWarningNoPoloSuffix')}</span>
                  </>
                )}
              </p>
            </div>
          </div>
        </SectionCard>
      )}

      {/* ── Processo Seletivo ── */}
      {selectiveProcess != null && (
        <SectionCard
          header={
            <SectionTitle
              icon={AcademicCapIcon}
              title={t('enrollmentAcademic.sectionSelectiveTitle')}
              description={t('enrollmentAcademic.sectionSelectiveDescription')}
              action={
                <Button
                  hierarchy="tertiary"
                  size="sm"
                  leadingIcon={PencilSquareIcon}
                  onClick={onEditSelectiveProcess}
                >
                  {t('enrollmentAcademic.editSelective')}
                </Button>
              }
            />
          }
        >
          <div className="grid grid-cols-2 gap-xxxs md:grid-cols-4">
            {/* STATUS */}
            <div className="flex flex-col gap-quark">
              <span className="ts-caption-xxs tracking-wider text-ctx-content-placeholder uppercase">
                {t('enrollmentAcademic.fieldSpStatus')}
              </span>
              <div>
                <Tag
                  label={t(`selectiveProcessStatus.${selectiveProcess.status}`)}
                  size="sm"
                  status={selectiveStatusTag(selectiveProcess.status)}
                  custom={{ text: 'text-nowrap' }}
                />
              </div>
            </div>

            <InfoField
              label={t('enrollmentAcademic.fieldSpAdmissionType')}
              value={t(`admissionType.${selectiveProcess.admissionType}`)}
            />

            <InfoField
              label={t('enrollmentAcademic.fieldSpIsExam')}
              value={
                selectiveProcess.isEntranceExam
                  ? t('enrollmentAcademic.yesLabel')
                  : t('enrollmentAcademic.noLabel')
              }
            />

            <InfoField
              label={t('enrollmentAcademic.fieldSpAttended')}
              value={
                selectiveProcess.isEntranceExam
                  ? selectiveProcess.isAttendEntranceExam
                    ? t('enrollmentAcademic.yesLabel')
                    : t('enrollmentAcademic.noLabel')
                  : undefined
              }
            />

            {/* DOCUMENTAÇÃO APROVADA */}
            <div className="flex flex-col gap-quark">
              <span className="ts-caption-xxs tracking-wider text-ctx-content-placeholder uppercase">
                {t('enrollmentAcademic.fieldSpDocApproved')}
              </span>
              <div>
                <Tag
                  label={
                    selectiveProcess.isDocumentationApproved
                      ? t('enrollmentAcademic.yesLabel')
                      : t('enrollmentAcademic.noLabel')
                  }
                  size="sm"
                  status={selectiveProcess.isDocumentationApproved ? 'positive' : 'negative'}
                  custom={{ text: 'text-nowrap' }}
                />
              </div>
            </div>

            <InfoField
              label={t('enrollmentAcademic.fieldSpExamDate')}
              value={formatDate(selectiveProcess.entranceExamDate ?? null)}
            />

            <InfoField
              label={t('enrollmentAcademic.fieldSpInterviewDate')}
              value={
                selectiveProcess.interviewDate
                  ? formatInterviewDateTime(selectiveProcess.interviewDate)
                  : null
              }
              note={
                selectiveProcess.interviewerName
                  ? t('enrollmentAcademic.fieldSpInterviewNote', {
                      name: selectiveProcess.interviewerName,
                    })
                  : null
              }
            />

            <InfoField
              label={t('enrollmentAcademic.fieldSpDocRejectReason')}
              value={selectiveProcess.documentationRejectReason ?? undefined}
            />

            <InfoField
              label={t('enrollmentAcademic.fieldSpExamGrade')}
              value={
                selectiveProcess.entranceExamGrade != null
                  ? formatGrade(selectiveProcess.entranceExamGrade)
                  : undefined
              }
            />

            <InfoField
              label={t('enrollmentAcademic.fieldSpEssayGrade')}
              value={
                selectiveProcess.essayGrade != null
                  ? formatGrade(selectiveProcess.essayGrade)
                  : undefined
              }
            />
          </div>
        </SectionCard>
      )}
    </div>
  );
}
